/**
 * DocBits PWA - Service Worker Registration
 *
 * This file handles service worker registration, updates, and lifecycle events.
 * It provides update notifications and ensures users always run the latest version.
 *
 * Key Features:
 * - Registers service worker on app load
 * - Detects when new version is available
 * - Shows update notification to users
 * - Handles update acceptance and app reload
 * - Manages service worker lifecycle
 *
 * @see docs/PWA.md for complete documentation
 */

import { register } from 'register-service-worker';
import logger from 'src/utils/logger';


/**
 * Clean up legacy service worker registrations
 *
 * Removes stale /service-worker.js registrations from before Dec 19, 2025
 * when the app incorrectly registered service-worker.js instead of sw.js.
 *
 * @see CLAUDE.md section "PWA Service Worker Migration"
 */
async function cleanupLegacyServiceWorkers() {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();

    for (const registration of registrations) {
      const scriptURL = registration.active?.scriptURL ||
                        registration.waiting?.scriptURL ||
                        registration.installing?.scriptURL || '';

      if (scriptURL.includes('service-worker.js')) {
        await registration.unregister();

        if (process.env.NODE_ENV !== 'production') {
          logger.info('[PWA] Cleaned up legacy service-worker.js registration');
        }
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      logger.warn('[PWA] Legacy cleanup error (non-blocking):', error);
    }
  }
}

/**
 * Attempt to re-register the service worker silently
 * Used when registration becomes invalid/deleted
 */
async function reRegisterServiceWorker(message = 'DocBits updated') {
  try {
    // IMPORTANT: Always use '/sw.js' (Quasar-generated filename)
    // Previously used '/service-worker.js' which caused MIME type errors
    // because the file doesn't exist (Quasar generates sw.js, not service-worker.js)
    // Changed in commit 7296e23325 (Dec 19, 2025)
    // See CLAUDE.md section "PWA Service Worker Migration" for details
    const newReg = await navigator.serviceWorker.register('/sw.js');
    logger.info('[PWA] Service worker re-registered successfully');
    window.__sw_registration = newReg;

    logger.info(`[PWA] ${message}`);

    return newReg;
  } catch (err) {
    logger.error('[PWA] Re-registration failed:', err);
    return null;
  }
}

// Only register service worker in production AND when the runtime supports it.
//
// `navigator.serviceWorker` is `undefined` on insecure origins — that's
// HTTPS-only with `localhost`/`127.0.0.1` as the lone HTTP exception. A
// production build served over plain HTTP (e.g. local network IP for
// device testing, behind a reverse proxy that terminates TLS upstream
// without forwarding it) will hit this branch and crash on the
// unguarded `navigator.serviceWorker.addEventListener` calls below
// before Vue gets a chance to mount. Hard-gate the entire block on
// SW availability so the app degrades gracefully to a plain SPA.
const hasSW = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

if (process.env.NODE_ENV === 'production' && hasSW) {
  // Clean up legacy service workers before registering new one
  // Removes stale /service-worker.js registrations (pre-Dec 2025)
  cleanupLegacyServiceWorkers();

  // SW lifecycle handlers — kept outside the registration call so the
  // load-event gate below stays compact.
  const swHandlers = {
    ready(registration) {
      logger.info('[PWA] Service worker is active and ready.');
      window.__sw_registration = registration;

      // Check for updates immediately
      registration.update().catch(err => {
        logger.warn('[PWA] Initial update check failed:', err);
      });
    },

    /**
     * Set up periodic update checks with auto-recovery.
     */
    registered(registration) {
      logger.info('[PWA] Service worker has been registered.');

      const updateInterval = setInterval(async () => {
        if (!navigator.onLine) return;

        try {
          const currentReg = await navigator.serviceWorker.getRegistration('/');

          if (!currentReg) {
            logger.info('[PWA] Registration lost, re-registering...');
            clearInterval(updateInterval);
            await reRegisterServiceWorker('DocBits connection restored');
            return;
          }

          await currentReg.update();
        } catch (err) {
          const errorMsg = err.message || '';
          if (errorMsg.includes('Not found')) {
            logger.info('[PWA] Registration invalid, recovering...');
            await reRegisterServiceWorker('DocBits updated');
          } else {
            logger.warn('[PWA] Update check failed:', err);
          }
        }
      }, 5 * 60 * 1000); // 5 minutes

      window.__sw_update_interval = updateInterval;
    },

    cached() {
      logger.info('[PWA] Content cached for offline use.');
    },

    updatefound() {
      logger.info('[PWA] Downloading update...');
    },

    /**
     * New content is available — dispatch an event for Vue components to handle.
     * The useUpdateNotification composable listens for this event.
     */
    updated(registration) {
      logger.info('[PWA] New content available.');
      // Wholesale-purge asset-cache to recover users whose runtime cache
      // was poisoned by SW versions before the cacheWillUpdate filter shipped.
      if (typeof caches !== 'undefined') {
        caches.delete('asset-cache').catch(() => { /* non-blocking */ });
      }
      window.dispatchEvent(new CustomEvent('pwa-update-available', {
        detail: {
          registration,
          newVersion: process.env.APP_VUE_CURRENT_VERSION || 'unknown',
        },
      }));
    },

    offline() {
      logger.warn('[PWA] App running in offline mode.');
    },

    error(error) {
      logger.error('[PWA] Registration error:', error);
      if (error.message?.includes('SecurityError')) {
        logger.error('[PWA] PWA features require HTTPS');
      }
    },
  };

  // Defer SW registration until AFTER window.load. The standard fix for
  // the first-install SW lifecycle race: with skipWaiting + clientsClaim
  // (which we want for fast deploy roll-out on returning users), a SW
  // installing during page load can take control and route in-flight
  // chunk requests through a fetch handler that's not yet ready, leaving
  // the page stuck on a loader. Registering after `load` means the
  // critical JS has already been requested before the SW exists, so the
  // takeover can't interrupt the boot chain. Returning users still get
  // instant updates — they just apply on the NEXT page load.
  //
  // IMPORTANT: Always use '/sw.js' (Quasar-generated filename — see
  // CLAUDE.md "PWA Service Worker Migration" for the history).
  const registerSW = () => register('/sw.js', swHandlers);

  if (document.readyState === 'complete') {
    registerSW();
  } else {
    window.addEventListener('load', registerSW, { once: true });
  }

  // Log controller changes
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    logger.info('[PWA] New service worker active');
  });

  // Handle messages from service worker
  navigator.serviceWorker.addEventListener('message', event => {
    if (event.data?.type === 'ERROR') {
      logger.error('[PWA] Service worker error:', event.data.error);
    }
  });

  // Online/offline event listeners
  window.addEventListener('online', () => {
    logger.info('[PWA] Back online');
    // Online/offline notifications handled by Vue components listening for events

    // Check for updates
    window.__sw_registration?.update().catch(() => {});
  });

  window.addEventListener('offline', () => {
    logger.warn('[PWA] Offline');

    // Dispatch event so components can handle offline state
    window.dispatchEvent(new CustomEvent('pwa-offline'));
  });

  // Startup health check - re-register if needed
  setTimeout(async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration('/');
      if (!reg) {
        await reRegisterServiceWorker('DocBits initialized');
      }
    } catch (err) {
      logger.error('[PWA] Startup check failed:', err);
    }
  }, 2000);

} else if (process.env.NODE_ENV === 'production' && !hasSW) {
  // Production build on an insecure origin (HTTP, non-localhost). PWA
  // features unavailable; app continues as a plain SPA.
  logger.warn('[PWA] Service worker unavailable on this origin (HTTPS required); running as SPA');
} else {
  logger.info('[PWA] Service worker disabled (development mode)');
}

// Utility exports
export function unregisterServiceWorker() {
  if (window.__sw_update_interval) {
    clearInterval(window.__sw_update_interval);
    window.__sw_update_interval = null;
  }

  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker.getRegistrations()
      .then(regs => Promise.all(regs.map(r => r.unregister())));
  }
  return Promise.resolve();
}

export function checkForUpdates() {
  return window.__sw_registration?.update() || Promise.reject(new Error('No service worker'));
}

export function getServiceWorkerRegistration() {
  return window.__sw_registration || null;
}

export { cleanupLegacyServiceWorkers };
